import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { fmtLog } from "@/lib/tz";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const CRIMSON = "#bf3b4f";

// Office-only log of conversations with / about a participant. The API rejects
// Support Worker tokens; `canEdit` only hides the form for read-only office roles.
export function ParticipantOfficeNotes({ participant, canEdit, user, isAdmin }) {
  const [notes, setNotes] = useState(null);
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.participantOfficeNotes(participant).then(setNotes).catch(() => setNotes([]));
  useEffect(() => { setNotes(null); load(); }, [participant]);

  const add = async () => {
    if (!subject.trim()) { toast.error("Subject is required."); return; }
    if (!note.trim()) { toast.error("Enter a note."); return; }
    setBusy(true);
    try {
      await api.participantOfficeNoteAdd(participant, { subject, note });
      setSubject(""); setNote(""); toast.success("Office note added."); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add note."); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this office note?")) return;
    try { await api.participantOfficeNoteDelete(participant, id); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete."); }
  };

  return (
    <div className="mt-4" data-testid="participant-office-notes">
      <div className="text-xs text-slate-500 flex items-center gap-1.5 mb-3">
        <Lock className="h-3.5 w-3.5" />Office staff only. Not visible to support workers or on the participant portal.
      </div>
      {canEdit && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 mb-4" data-testid="po-note-form">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (required), e.g. Phone call with plan manager" className="h-9 text-sm bg-white mb-2" data-testid="po-note-subject" />
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was discussed, and who with…" className="text-sm bg-white" rows={3} data-testid="po-note-input" />
          <div className="flex justify-end mt-2">
            <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={add} disabled={busy} data-testid="po-note-add">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}Add note
            </Button>
          </div>
        </div>
      )}
      {notes === null ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : notes.length === 0 ? (
        <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">No office notes yet.</div>
      ) : (
        <div className="space-y-2" data-testid="po-notes-list">
          {notes.map((n) => {
            const canDel = canEdit && (isAdmin || n.author === user?.username);
            return (
              <div key={n.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`po-note-${n.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-medium text-slate-800 truncate">{n.subject}</span>
                  {canDel && <button onClick={() => del(n.id)} className="text-slate-400 hover:text-rose-600 p-1 rounded hover:bg-rose-50 shrink-0" title="Delete" data-testid={`po-note-del-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{n.note}</div>
                <div className="text-[10px] text-slate-400 mt-1.5">{n.author_name || n.author} · {fmtLog(n.created_at)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
